import { useState } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import {
  btnSize,
  btnType,
  Button,
  colors,
  fontWeight,
  Heading,
  headingLevel,
  Input,
  inputSize,
  Text,
  TextError,
  textLevel,
} from '../../../../packages';
import { Drawer, drawerPosition, drawerType } from '../../../../packages/ui/organisms/drawer';
import { useStyles } from '../ClaimDetail.styles';

const defaultData = { name: '', taxId: '', phone: '', fax: '', email: '' };

const labels = {
  name: 'Submitter',
  taxId: 'Tax ID',
  phone: 'Phone',
  fax: 'Fax',
  email: 'Email',
};

const SubmitterDrawer = ({ data, open, onClose, onSubmit }) => {
  const classes = useStyles();
  const [formData, setFormData] = useState({ ...data });
  const [errors, setErrors] = useState(defaultData);

  const onChangeField = field => e => {
    if (errors[field]) {
      setErrors(s => ({ ...s, [field]: '' }));
    }
    setFormData(old => ({
      ...old,
      [field]: e.target?.value,
    }));
  };

  const onChangeSubmitter = () => {
    onSubmit(formData, 'submitter');
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      variant={drawerType.FORM}
      position={drawerPosition.RIGHT}
      title="Update Billing Provider details"
      submitBtnTitle="Update"
      onSubmit={onChangeSubmitter}
    >
      {Object.keys(data)
        .filter(d => !['state', 'email', 'candidBillingProvider'].includes(d))
        .filter(Boolean)
        .map((d, index) => (
          <Box className={classes.inputWrapper} key={`${d}-${index}`}>
            <label htmlFor={d}>
              <Text level={textLevel.S} weight={fontWeight.BOLD}>
                {labels[d]}
              </Text>
            </label>
            <Input
              name={d}
              value={formData[d] || ''}
              onChange={onChangeField(d)}
              size={inputSize.M}
              placeholder={`Enter your ${labels[d]}`}
            />
            {errors[d] && <TextError errorMsg={errors[d]} />}
          </Box>
        ))}
    </Drawer>
  );
};

const Submitter = ({ onSubmit, data = defaultData }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const Col = ({ label, value }) => (
    <Box display="flex" flexDirection="column" minWidth="160px">
      <Text className={classes.colLabel} level={textLevel.S} weight={fontWeight.SEMI_BOLD}>
        {label}
      </Text>
      <Text className={classes.marginTop8} level={textLevel.M} weight={fontWeight.MEDIUM}>
        {value || 'N/A'}
      </Text>
    </Box>
  );

  return (
    <Box
      sx={{
        background: colors.white,
        boxShadow:
          '0px 4px 8px rgba(0, 0, 0, 0.04), 0px 0px 2px rgba(0, 0, 0, 0.06), 0px 0px 1px rgba(0, 0, 0, 0.04)',
        borderRadius: 2,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" pl={3} py="12px" pr={2}>
        <Heading level={headingLevel.S} weight={fontWeight.SEMI_BOLD}>
          Billing Provider
        </Heading>
        <Button
          className={classes.btn}
          variant={btnType.TEXT}
          onClick={() => setOpen(true)}
          size={btnSize.SMALL}
        >
          Change billing provider
        </Button>
      </Box>
      <Divider />

      <Box display="flex" p={4} flexWrap="wrap" gap={3} rowGap={4}>
        <Col label="Organization Name" value={data?.name} />
        {data?.candidBillingProvider?.npi && <Col label="NPI" value={data?.candidBillingProvider?.npi} />}
        <Col label="Tax id" value={data?.taxId} />
        {data?.candidBillingProvider?.taxonomy_code && (
          <Col label="Taxonomy Code" value={data?.candidBillingProvider?.taxonomy_code} />
        )}
        {data?.candidBillingProvider?.address && (
          <>
            <Col label="Address 1" value={data?.candidBillingProvider?.address?.address1} />
            <Col label="Address 2" value={data?.candidBillingProvider?.address?.address2} />
            <Col label="City" value={data?.candidBillingProvider?.address?.city} />
            <Col label="State" value={data?.candidBillingProvider?.address?.state ?? data?.state} />
            <Col label="Zip Code" value={data?.candidBillingProvider?.address?.zip_code} />
          </>
        )}
      </Box>
      {open && <SubmitterDrawer data={data} open={open} onClose={() => setOpen(false)} onSubmit={onSubmit} />}
    </Box>
  );
};

export default Submitter;
